// angular
import { DecimalPipe } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { formatDistanceToNow } from 'date-fns';
import { AuctionsService } from '@app/+auctions/shared/services/auctions.service';
import {
  IAuction,
  IAuctionParticipator,
} from '@app/+auctions/shared/types/auctions.type';
import { TranslateDataPipe } from '@app/shared/pipes/translate-data.pipe';
import { currencyToSymbol } from '@app/shared/utils/currency-to-symbol.util';

export interface IBidLogEntry {
  id: string;
  name: string;
  initial: string;
  price: number;
  createdAt?: string;
  isBasePrice?: boolean;
  isLeading?: boolean;
}

@Component({
  selector: 'pwj-auction-bids-live-tab',
  standalone: true,
  imports: [TranslateModule, TranslateDataPipe, DecimalPipe],
  templateUrl: './auction-bids-live-tab.component.html',
  styleUrl: './auction-bids-live-tab.component.scss',
})
export class AuctionBidsLiveTabComponent {
  protected readonly currencyToSymbol = currencyToSymbol;

  auction = input.required<IAuction>();

  bidEntries = computed(() =>
    this.buildBidEntries(this._auctionsService.auctionParticipators())
  );

  private readonly _auctionsService = inject(AuctionsService);
  private readonly _translateService = inject(TranslateService);
  private readonly _translateData = inject(TranslateDataPipe);

  getBidderName(participator: IAuctionParticipator): string {
    if (participator?.person?.business?.title) {
      return (
        this._translateData.transform(participator.person.business.title) || '-'
      );
    }

    if (participator?.person?.first_name) {
      return `${participator.person.first_name} ${participator.person.last_name || ''}`.trim();
    }

    return '-';
  }

  getInitial(name: string): string {
    return name?.trim()?.charAt(0)?.toUpperCase() || '?';
  }

  getRelativeTime(date?: string): string {
    if (!date) {
      return '-';
    }

    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch {
      return '-';
    }
  }

  private buildBidEntries(participators: IAuctionParticipator[]): IBidLogEntry[] {
    const auction = this.auction();
    const sortedBids = [...participators].sort(
      (a, b) => (b.price || 0) - (a.price || 0)
    );

    const entries: IBidLogEntry[] = sortedBids.map((participator, index) => {
      const name = this.getBidderName(participator);
      return {
        id: `bid-${participator.created_at || index}`,
        name,
        initial: this.getInitial(name),
        price: participator.price || 0,
        createdAt: participator.created_at,
        isLeading: index === 0,
      };
    });

    if (auction?.base_price) {
      entries.push({
        id: 'base-price',
        name: this._translateService.instant('auctions.initialBasePrice'),
        initial: 'B',
        price: auction.base_price,
        isBasePrice: true,
      });
    }

    return entries;
  }
}
